import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createBasket, moveTopic, addBasketToWorkspace, fetchWorkspaceDetails, setWorkspaceLoaded, moveBasketToTop, setBaskets } from '../../store/workspacesSlice';
import Header from './components/Header';
import Searchbar from './components/Searchbar';
import BasketGrid from './components/BasketGrid';
import MoveButton from './components/MoveButton';
import TopicInfo from './components/TopicInfo';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AddBasketModal from '../AddBasketModal';
import RelationDescriptionModal from './components/RelationDescriptionModal';

const BASKET_FILTERS = {
  ALL: 'All Baskets',
  SINGLE: 'Single Item',
  DOUBLE: 'Two Items',
  MULTIPLE: 'Three or More',
  EMPTY: 'Empty'
};

const TabButton = ({ active, onClick, children, count, className = '' }) => (
  <button
    onClick={onClick}
    className={`
      relative px-3 py-1.5 text-sm font-medium transition-all duration-200
      ${active 
        ? 'text-neutral-900 bg-white shadow-sm' 
        : 'text-neutral-600 hover:text-neutral-800 hover:bg-white/50'
      }
      rounded-lg border
      ${active 
        ? 'border-neutral-900 shadow-sm' 
        : 'border-transparent hover:border-neutral-300'
      }
      ${className}
    `}
  >
    <span className="relative z-10 flex items-center gap-1.5">
      {children}
      {count !== undefined && (
        <span className={`
          px-1.5 py-0.5 text-xs rounded-full
          ${active 
            ? 'bg-butter-500 text-neutral-900' 
            : 'bg-neutral-200 text-neutral-600'
          }
        `}>
          {count}
        </span>
      )}
    </span>
  </button>
);

const getSearchPriority = (basket, searchTerm) => {
  if (!searchTerm) return 0;
  
  const lowercasedSearchTerm = searchTerm.toLowerCase();
  
  // Priority 1: Direct basket name match
  if (basket.name.toLowerCase().includes(lowercasedSearchTerm)) {
    return 1;
  }
  
  // Priority 2: Topic name match
  if (basket.topics?.some(topic => 
    topic.name.toLowerCase().includes(lowercasedSearchTerm)
  )) {
    return 2;
  }
  
  // Priority 3: Concept text match
  if (basket.topics?.some(topic => 
    topic.concepts?.some(concept => 
      concept.text.toLowerCase().includes(lowercasedSearchTerm)
    )
  )) {
    return 3;
  }
  
  return 4; // No match
};

const BasketSelectionInterface = ({ onClose, onBasketSelected, currentBasketId, topic, workspaceId: propWorkspaceId }) => {
  const dispatch = useDispatch();
  const baskets = useSelector(state => state.workspaces.currentWorkspace.baskets);
  const stateWorkspaceId = useSelector(state => state.workspaces.currentWorkspace.id);
  const currentWorkspace = useSelector(state => state.workspaces.currentWorkspace);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBasketId, setSelectedBasketId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const isWorkspaceLoaded = useSelector(state => state.workspaces.isWorkspaceLoaded);
  const [addBasketModalProps, setAddBasketModalProps] = useState({
    isOpen: false,
    initialName: '',
  });
  const [basketFilter, setBasketFilter] = useState(BASKET_FILTERS.ALL);
  const [showRelationModal, setShowRelationModal] = useState(false);

  // Use the prop workspaceId if available, otherwise fall back to state
  const workspaceId = propWorkspaceId || stateWorkspaceId;
  
  console.log('Workspace ID from props:', propWorkspaceId);
  console.log('Workspace ID from state:', stateWorkspaceId);
  console.log('Using workspace ID:', workspaceId);

  // Add this useEffect to fetch workspace details if needed
  useEffect(() => {
    if (workspaceId && !isWorkspaceLoaded) {
      dispatch(fetchWorkspaceDetails(workspaceId));
    }
  }, [workspaceId, isWorkspaceLoaded, dispatch]);

  const getFilterCounts = useMemo(() => {
    // First filter by search
    const searchFiltered = baskets.filter(basket => {
      if (!basket || basket.id === currentBasketId) return false;
      
      if (!searchTerm.trim()) return true;
      
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      
      // Check basket name
      if (basket.name.toLowerCase().includes(lowercasedSearchTerm)) return true;
      
      // Check topics and concepts
      return basket.topics.some(topic => {
        if (topic.name.toLowerCase().includes(lowercasedSearchTerm)) return true;
        return topic.concepts?.some(concept => 
          concept.text.toLowerCase().includes(lowercasedSearchTerm)
        );
      });
    });
    
    return {
      [BASKET_FILTERS.ALL]: searchFiltered.length,
      [BASKET_FILTERS.SINGLE]: searchFiltered.filter(b => (b.topics?.length || 0) === 1).length,
      [BASKET_FILTERS.DOUBLE]: searchFiltered.filter(b => (b.topics?.length || 0) === 2).length,
      [BASKET_FILTERS.MULTIPLE]: searchFiltered.filter(b => (b.topics?.length || 0) >= 3).length,
      [BASKET_FILTERS.EMPTY]: searchFiltered.filter(b => !b.topics?.length).length,
    };
  }, [baskets, currentBasketId, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const filteredBaskets = useMemo(() => {
    // First, filter out the current basket
    const otherBaskets = baskets.filter(basket => basket.id !== currentBasketId);

    // Apply item count filter
    const countFiltered = otherBaskets.filter(basket => {
      const itemCount = basket.topics?.length || 0;
      
      switch (basketFilter) {
        case BASKET_FILTERS.SINGLE:
          return itemCount === 1;
        case BASKET_FILTERS.DOUBLE:
          return itemCount === 2;
        case BASKET_FILTERS.MULTIPLE:
          return itemCount >= 3;
        case BASKET_FILTERS.EMPTY:
          return itemCount === 0;
        default:
          return true; // BASKET_FILTERS.ALL
      }
    });

    // Then apply search filter and sorting
    if (!searchTerm.trim()) return countFiltered;

    const lowercasedSearchTerm = searchTerm.toLowerCase();

    return countFiltered
      .filter(basket => {
        // Check basket name
        if (basket.name.toLowerCase().includes(lowercasedSearchTerm)) return true;

        // Check topic names
        if (basket.topics.some(topic => 
          topic.name.toLowerCase().includes(lowercasedSearchTerm)
        )) return true;

        // Check concept texts
        return basket.topics.some(topic => 
          topic.concepts?.some(concept => 
            concept.text.toLowerCase().includes(lowercasedSearchTerm)
          )
        );
      })
      .sort((a, b) => {
        const priorityA = getSearchPriority(a, searchTerm);
        const priorityB = getSearchPriority(b, searchTerm);
        
        // Sort by priority first
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        
        // If same priority, sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
  }, [baskets, searchTerm, currentBasketId, basketFilter]);

  const handleBasketSelect = (basketId) => {
    if (basketId !== currentBasketId) {
      setSelectedBasketId(prevId => prevId === basketId ? null : basketId);
    }
  };

  const handleMove = async () => {
    if (selectedBasketId && selectedBasketId !== currentBasketId) {
      setShowRelationModal(true);
    }
  };

  const handleMoveWithRelation = async (relationDescription) => {
    try {
      const moveResult = await dispatch(moveTopic({
        topicId: topic.id,
        fromBasketId: currentBasketId,
        toBasketId: selectedBasketId,
        workspaceId,
        relationDescription: relationDescription || ''
      })).unwrap();

      // Update baskets state
      const updatedBaskets = baskets.map(basket => {
        if (basket.id === currentBasketId) {
          return {
            ...basket,
            topics: basket.topics.filter(t => t.id !== topic.id),
            updated_at: new Date().toISOString()
          };
        }
        if (basket.id === selectedBasketId) {
          return {
            ...basket,
            topics: [...basket.topics, moveResult.topic],
            updated_at: new Date().toISOString()
          };
        }
        return basket;
      });

      // Move target basket to top
      const targetBasketIndex = updatedBaskets.findIndex(b => b.id === selectedBasketId);
      if (targetBasketIndex !== -1) {
        const [targetBasket] = updatedBaskets.splice(targetBasketIndex, 1);
        updatedBaskets.unshift(targetBasket);
      }

      dispatch(setBaskets(updatedBaskets));
      onClose();
      
      navigate(`/workspace/${workspaceId}`, {
        state: { 
          activeTab: 'Baskets',
          workspaceName: currentWorkspace?.name || 'Workspace',
          lastFetchedAt: Date.now()
        }
      });
    } catch (error) {
      console.error('Failed to move topic:', error);
      toast.error(error.message || 'Failed to move topic');
    }
  };

  const handleBasketMenuClick = (basket, action) => {
    // Implement the logic for handling basket menu actions
    switch (action) {
      case 'edit':
        // Handle edit action
        console.log('Edit basket:', basket.id);
        break;
      case 'delete':
        // Handle delete action
        console.log('Delete basket:', basket.id);
        break;
      case 'share':
        // Handle share action
        console.log('Share basket:', basket.id);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleNewBasketClick = () => {
    setAddBasketModalProps({
      isOpen: true,
      initialName: '',
    });
  };

  const handleCreateFromSearch = (searchTerm) => {
    setAddBasketModalProps({
      isOpen: true,
      initialName: searchTerm.trim(),
    });
  };

  const handleAddBasketSuccess = async (basketData) => {
    setAddBasketModalProps({ isOpen: false, initialName: '' });
    
    try {
      setIsCreating(true);
      
      // First, create the basket in the database
      const createdBasket = await dispatch(createBasket({
        name: basketData.name,
        description: basketData.description,
        workspace_id: workspaceId
      })).unwrap();
      
      // Check if the created basket has an ID
      if (!createdBasket.id) {
        throw new Error('Failed to create basket: Missing basket ID');
      }
      
      // Move the topic to the new basket
      const moveResult = await dispatch(moveTopic({
        topicId: topic.id,
        fromBasketId: currentBasketId,
        toBasketId: createdBasket.id,
        workspaceId
      })).unwrap();

      // Move the basket to top
      dispatch(moveBasketToTop({ basketId: createdBasket.id }));

      // Update the basket with the moved topic
      const basketWithTopic = {
        ...createdBasket,
        topics: [moveResult.topic]
      };

      // Add basket to workspace state
      dispatch(addBasketToWorkspace({ 
        workspaceId, 
        basket: basketWithTopic 
      }));

      // Close modals
      onClose();

      // Navigate to workspace detail
      navigate(`/workspace/${workspaceId}`, {
        state: { 
          activeTab: 'Baskets',
          workspaceName: currentWorkspace?.name || 'Workspace',
          shouldScrollToBasket: createdBasket.id
        }
      });
    } catch (error) {
      console.error('Failed to move topic:', error);
      toast.error(error.message || 'Failed to move topic');
    } finally {
      setIsCreating(false);
    }
  };

  // Update the filter tabs section
  const handleScroll = (e) => {
    const container = e.currentTarget;
    const isScrollable = container.scrollWidth > container.clientWidth;
    
    if (isScrollable) {
      container.style.paddingBottom = '4px'; // Add padding to show scrollbar
    } else {
      container.style.paddingBottom = '0px';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-3xl h-[90vh] flex flex-col">
        <Header onClose={onClose} />
        
        {/* Make TopicInfo more compact */}
        <div className="flex-shrink-0">
          <TopicInfo topic={topic} currentBasketId={currentBasketId} baskets={baskets} />
        </div>
        
        {/* Search and New Basket button container */}
        <div className="flex-shrink-0 flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-grow">
            <Searchbar 
              searchTerm={searchTerm} 
              setSearchTerm={setSearchTerm}
              onCreateFromSearch={handleCreateFromSearch}
              showCreateButton={filteredBaskets.length === 0}
              onClearSearch={handleClearSearch}
            />
          </div>
          <button
            onClick={handleNewBasketClick}
            disabled={isCreating || !isWorkspaceLoaded || !workspaceId}
            className={`
              h-10 px-4 bg-cranberry-700 text-white text-sm rounded-xl border-2 border-neutral-900 
              font-medium hover:bg-cranberry-600 transition-colors whitespace-nowrap flex items-center justify-center
              disabled:opacity-50 disabled:cursor-not-allowed
              ${(isCreating || !isWorkspaceLoaded || !workspaceId) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-cranberry-600'}
            `}
          >
            {!isWorkspaceLoaded ? 'Loading...' : '+ New Basket'}
          </button>
        </div>

        {/* Filter tabs - horizontally scrollable */}
        <div className="flex-shrink-0 mb-3">
          <div 
            className="flex overflow-x-auto hide-scrollbar" 
            onScroll={handleScroll}
            style={{ 
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <div className="flex gap-1.5 p-1 min-w-min">
              {Object.values(BASKET_FILTERS).map((filter) => (
                <TabButton
                  key={filter}
                  active={basketFilter === filter}
                  onClick={() => setBasketFilter(filter)}
                  count={getFilterCounts[filter]}
                  className="whitespace-nowrap"
                >
                  {filter.replace(' Baskets', '')}
                </TabButton>
              ))}
            </div>
          </div>
        </div>

        {/* Basket grid with flex-1 to take remaining space */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <BasketGrid 
            baskets={filteredBaskets} 
            selectedBasketId={selectedBasketId} 
            onBasketSelect={handleBasketSelect}
            currentBasketId={currentBasketId}
            searchTerm={searchTerm}
            onBasketMenuClick={handleBasketMenuClick}
          />
        </div>

        {/* Move button at the bottom */}
        <div className="flex-shrink-0 mt-4">
          <MoveButton 
            onClick={handleMove} 
            disabled={!selectedBasketId || selectedBasketId === currentBasketId} 
          />
        </div>

        <AddBasketModal
          isOpen={addBasketModalProps.isOpen}
          initialBasketName={addBasketModalProps.initialName}
          onClose={() => setAddBasketModalProps({ isOpen: false, initialName: '' })}
          workspaceId={workspaceId}
          onAdd={handleAddBasketSuccess}
        />
        
        <RelationDescriptionModal
          isOpen={showRelationModal}
          onClose={() => setShowRelationModal(false)}
          onSubmit={(description) => {
            setShowRelationModal(false);
            handleMoveWithRelation(description);
          }}
          topicName={topic.name}
          basketName={baskets.find(b => b.id === selectedBasketId)?.name || ''}
        />
      </div>
    </div>
  );
};

export default BasketSelectionInterface;
